/**
 * The /sell address step's time budget (job se-sell-hang-20261028).
 *
 * WHAT BROKE. A visitor typed 615 NW Franklin Ave, Bend and the sheet sat on
 * "Reading the Bend record" with no way forward. answerSellValue awaited the
 * limiter, the subject resolver, the comp ladder and one place answer PER grain
 * candidate, in series, with no deadline on any of them. A slow read anywhere
 * in that chain held the transition open, and because the form only advanced
 * when the action RETURNED, a slow, failed or never-answering action left the
 * visitor stuck on the reading state.
 *
 * THE RULE. The answer is a bonus, never a gate. Every read on the server gets
 * its own leash (below), and the reads that do not depend on each other run
 * together, so the action's worst case is the sum of three leashes, not the sum
 * of every query. The browser holds a hard cap above that, so even a request
 * that never answers (a dropped connection, a function killed at its limit, a
 * server action queued behind another) lets the sheet advance to the timing
 * question inside SELL_ANSWER_CLIENT_CAP_MS.
 *
 * §0: a read that ran out of time is UNKNOWN, not empty. The action drops the
 * figure it could not read; it never prints a zero for it.
 */

/** Per-read server leashes, in milliseconds. */
export const SELL_ANSWER_BUDGET_MS = {
  /** The per-IP limiter (a network round trip to the rate-limit store). */
  limiter: 1_000,
  /** Matching the typed address to the MLS record. */
  subject: 3_500,
  /** The comp ladder and the place answers, which run side by side. */
  reads: 4_000,
} as const

/** The server's worst case: the three leashes in series. */
export const SELL_ANSWER_SERVER_WORST_MS =
  SELL_ANSWER_BUDGET_MS.limiter + SELL_ANSWER_BUDGET_MS.subject + SELL_ANSWER_BUDGET_MS.reads

/**
 * The browser's hard cap on the address step. Above the server's worst case so
 * a healthy slow answer still lands, and at the ~12s the sheet promises.
 */
export const SELL_ANSWER_CLIENT_CAP_MS = 11_000

/**
 * Resolve `promise`, or `fallback` when it rejects or has not settled inside
 * `ms`. Never rejects, so the caller's next step always runs. Browser-safe (no
 * Next server imports), which is why the form uses this and not
 * lib/with-timeout-fallback.
 */
export function settleWithin<T>(promise: Promise<T>, fallback: T, ms: number): Promise<T> {
  return new Promise<T>((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      () => {
        clearTimeout(timer)
        resolve(fallback)
      },
    )
  })
}
